import _defineProperty from "/Users/matt/dev/github.com/industriousapps/excelkits-client/node_modules/.pnpm/next@13.5.7_@babel+core@7.25.2_react-dom@18.3.1_react@18.3.1__react@18.3.1_sass@1.79.3/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/defineProperty.js";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import { useMemo } from 'react';
import flattenDeep from 'lodash/flattenDeep';
import { getSelectionGroupItemByIndex } from '@/components/Editor/Form/utils';
import { DEFAULT_DATA_LABEL_OPTIONS, overrideConfig } from '@/components/widget/visualizations/PieDoughnut/utils';
import { usePieDoughnutStyles } from '@/components/widget/visualizations/PieDoughnut/stylesTheme';
import { labelsSelectionConfig } from '@/components/widget/visualizations/PieDoughnut/settings';
import { selectionConfig, DEFAULT_COLORS, useData } from '@/components/widget/visualizations/PieDoughnut';
import useSelections from '@/components/widget/misc/useSelections';
import useSubscribeWidgetResults from '@/components/widget/visualizations/hooks/useSubscribeWidgetResults';
import useSelectionLabels from '@/components/widget/misc/useSelectionLabels';
import ColorPalletMenu from '@/components/Editor/Form/fields/ColorPalletMenu';
import Headings from '@/components/Editor/Form/fields/Headings';
import Animation from '@/components/Editor/Form/fields/Animation';
import DataLabels from '@/components/Editor/Form/fields/DataLabels';
import Collapsible from '@/components/Editor/Form/fields/common/Collapsible';
import { TemplateRevealPill } from '@/components/Editor/Form/fields/Template';
import ChartType from '@/components/Editor/Form/fields/ChartType';
import Legend from './Legend';
import { CHART_TYPES } from './settings';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";

const WidgetEditFormPieDoughnutChartOptions = _ref => {
  var _configWithOverrides$, _config$globalSeriesO, _globalSeriesOptions$, _globalSeriesOptions$2, _globalSeriesOptions$3, _globalSeriesOptions$4, _globalSeriesOptions$5, _globalSeriesOptions$6, _globalSeriesOptions$7, _configWithOverrides$2;

  let {
    widgetId,
    sectionId,
    reportingWidgetId,
    config,
    pillVariant,
    onChange,
    onEditSelections
  } = _ref;
  const nameSpace = `${widgetId}-${sectionId}`;
  const templateFieldId = `${nameSpace}-template`;
  const {
    defaultStyles,
    themeStyles,
    themeCtx: {
      colorPallet: defaultColorPallet = DEFAULT_COLORS
    }
  } = usePieDoughnutStyles(config);
  const configWithOverrides = useMemo(() => overrideConfig(config), [config]);
  const {
    colorPallet = defaultColorPallet,
    selections = '',
    selectionsLabeled = true,
    selectionsStrategy = 'default',
    labelsSelections,
    seriesOptions = [],
    collectionOptions = [],
    globalSeriesOptions
  } = configWithOverrides; // Get current selection results

  const results = useSubscribeWidgetResults(reportingWidgetId); // Apply selections to results

  const {
    selectionResults,
    resultsUpdated
  } = useSelections({
    results,
    selections,
    selectionsLabeled,
    selectionConfig
  });
  const {
    selectionLabels,
    labelsSelectionsResult
  } = useSelectionLabels({
    results,
    resultsUpdated,
    labelsSelectionConfig,
    labelsSelections
  }); // Combine results with rendering styles

  const {
    legendData
  } = useData({
    results: selectionResults,
    seriesOptions,
    globalSeriesOptions,
    collectionOptions,
    colorPallet,
    selectionLabels,
    selectionsStrategy,
    themeStyles
  });
  const exampleValue = useMemo(() => {
    const exampleSelection = getSelectionGroupItemByIndex(flattenDeep(selectionResults), 0);
    return typeof exampleSelection.value !== 'undefined' ? exampleSelection.value : 10;
  }, [selectionResults]);
  const exampleLabel = useMemo(() => {
    const exampleSelection = getSelectionGroupItemByIndex(flattenDeep(labelsSelectionsResult), 0);
    return typeof exampleSelection.value !== 'undefined' ? exampleSelection.value : 'N/A';
  }, [labelsSelectionsResult]);
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(ColorPalletMenu, {
      label: "Colors",
      colors: colorPallet,
      palletColorOverrides: config.palletColorOverrides,
      openButtonVariant: pillVariant,
      onOverridePalletColors: (name, colors) => onChange('palletColorOverrides', {
        [name]: colors
      }),
      onChange: colorPallet => onChange('', {
        colorPallet
      })
    }), /*#__PURE__*/_jsx(TemplateRevealPill, {
      id: templateFieldId,
      label: "Number Format",
      caption: "Default formatting. Can be overwritten by other formatting settings.",
      exampleValue: exampleValue,
      template: (_configWithOverrides$ = configWithOverrides.globalSeriesOptions) === null || _configWithOverrides$ === void 0 ? void 0 : _configWithOverrides$.template,
      openButtonVariant: pillVariant,
      onChange: value => onChange('globalSeriesOptions', {
        template: value
      })
    }), /*#__PURE__*/_jsx(ChartType, {
      chartType: configWithOverrides.chartType,
      defaultChartType: "doughnut",
      options: CHART_TYPES,
      openButtonVariant: pillVariant,
      onChange: chartType => onChange('', {
        chartType
      })
    }), /*#__PURE__*/_jsx(Headings, {
      nameSpace: nameSpace,
      widgetId: widgetId,
      sectionId: sectionId,
      hasHeading: configWithOverrides.hasHeading,
      title: configWithOverrides.title,
      openButtonVariant: pillVariant,
      onChange: updates => onChange('', updates)
    }), /*#__PURE__*/_jsx(Legend, _objectSpread({
      nameSpace: nameSpace,
      legendPosition: configWithOverrides.legendPosition,
      legendType: configWithOverrides.legendType,
      legends: config.legends,
      legendsCount: legendData.length,
      exampleLabel: exampleLabel,
      template: (_config$globalSeriesO = config.globalSeriesOptions) === null || _config$globalSeriesO === void 0 ? void 0 : _config$globalSeriesO.labelsTemplate,
      openButtonVariant: pillVariant,
      onChange: updates => onChange('', updates),
      onChangeTemplate: value => onChange('globalSeriesOptions', {
        labelsTemplate: value
      })
    }, onEditSelections && {
      onEditSelections: () => onEditSelections('labels')
    })), /*#__PURE__*/_jsx(DataLabels, {
      nameSpace: nameSpace,
      exampleValue: exampleValue,
      display: globalSeriesOptions === null || globalSeriesOptions === void 0 ? void 0 : (_globalSeriesOptions$ = globalSeriesOptions.dataLabels) === null || _globalSeriesOptions$ === void 0 ? void 0 : _globalSeriesOptions$.display,
      anchoring: globalSeriesOptions === null || globalSeriesOptions === void 0 ? void 0 : (_globalSeriesOptions$2 = globalSeriesOptions.dataLabels) === null || _globalSeriesOptions$2 === void 0 ? void 0 : _globalSeriesOptions$2.anchoring,
      alignment: globalSeriesOptions === null || globalSeriesOptions === void 0 ? void 0 : (_globalSeriesOptions$3 = globalSeriesOptions.dataLabels) === null || _globalSeriesOptions$3 === void 0 ? void 0 : _globalSeriesOptions$3.alignment,
      rotation: globalSeriesOptions === null || globalSeriesOptions === void 0 ? void 0 : (_globalSeriesOptions$4 = globalSeriesOptions.dataLabels) === null || _globalSeriesOptions$4 === void 0 ? void 0 : _globalSeriesOptions$4.rotation,
      displayPercentages: globalSeriesOptions === null || globalSeriesOptions === void 0 ? void 0 : (_globalSeriesOptions$5 = globalSeriesOptions.dataLabels) === null || _globalSeriesOptions$5 === void 0 ? void 0 : _globalSeriesOptions$5.displayPercentages,
      template: (globalSeriesOptions === null || globalSeriesOptions === void 0 ? void 0 : (_globalSeriesOptions$6 = globalSeriesOptions.dataLabels) === null || _globalSeriesOptions$6 === void 0 ? void 0 : _globalSeriesOptions$6.template) || (globalSeriesOptions === null || globalSeriesOptions === void 0 ? void 0 : globalSeriesOptions.template),
      color: globalSeriesOptions === null || globalSeriesOptions === void 0 ? void 0 : (_globalSeriesOptions$7 = globalSeriesOptions.dataLabels) === null || _globalSeriesOptions$7 === void 0 ? void 0 : _globalSeriesOptions$7.color,
      colorPallet: colorPallet,
      defaults: {
        color: defaultStyles === null || defaultStyles === void 0 ? void 0 : defaultStyles.chartDataLabelColor,
        anchoring: DEFAULT_DATA_LABEL_OPTIONS.anchor,
        alignment: DEFAULT_DATA_LABEL_OPTIONS.align
      },
      openButtonVariant: pillVariant,
      hasPercentagesToggle: true,
      onChange: update => {
        var _config$globalSeriesO2;

        return onChange('globalSeriesOptions', {
          dataLabels: _objectSpread(_objectSpread({}, (_config$globalSeriesO2 = config.globalSeriesOptions) === null || _config$globalSeriesO2 === void 0 ? void 0 : _config$globalSeriesO2.dataLabels), update)
        });
      }
    }), /*#__PURE__*/_jsx(Animation, {
      nameSpace: nameSpace,
      animation: configWithOverrides.animation,
      openButtonVariant: pillVariant,
      onChange: update => onChange('animation', update)
    }), /*#__PURE__*/_jsx(Collapsible, {
      nameSpace: nameSpace,
      collapsible: (_configWithOverrides$2 = configWithOverrides.globalSeriesOptions) === null || _configWithOverrides$2 === void 0 ? void 0 : _configWithOverrides$2.collapsible,
      openButtonVariant: pillVariant,
      onChange: update => onChange('globalSeriesOptions', update)
    })]
  });
};

export default WidgetEditFormPieDoughnutChartOptions;